// FundingGroups — shared 4-card intent-based grouping for Financial Aid.
// Used on both the home page (replaces the legacy accordion) and the
// Financial Aid landing page so the UX stays in lockstep. Each item links
// directly to a sub-page or to a #anchor on /financial-aid where the
// detailed copy already lives.
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";

export const FUNDING_GROUPS = [
  {
    title: "Loans & Grants",
    items: [
      { label: "Government Student Loans (OSAP)", to: "https://www.ontario.ca/page/osap-ontario-student-assistance-program", external: true },
      { label: "Better Jobs Ontario", to: "/financial-aid/better-jobs-ontario" },
      { label: "Ontario Job Grant", to: "https://www.ontario.ca/page/ontario-job-grant", external: true },
      { label: "Out-of-Province Students", to: "/financial-aid/out-of-province" },
    ],
  },
  {
    title: "Personal Financing",
    items: [
      { label: "Alternative Student Financing Options", to: "/financial-aid/alternative-student-financing-options" },
      { label: "College Interest-Free Financing", to: "/financial-aid#interest-free-financing" },
      { label: "Bank Solutions", to: "/financial-aid#bank-solutions" },
      { label: "Windmill Microlending", to: "/financial-aid/windmill-microlending" },
    ],
  },
  {
    title: "Education Savings & Tax Benefits",
    items: [
      { label: "Registered Education Savings Plans (RESP)", to: "/financial-aid#resp" },
      { label: "Lifelong Learning Plan", to: "/financial-aid#lifelong-learning-plan" },
    ],
  },
  {
    title: "Bursaries",
    items: [
      { label: "Cardiology Technologist Bursary", to: "/financial-aid/cardiology-bursary" },
      { label: "Alumni Bursary", to: "/financial-aid/alumni-bursary" },
    ],
  },
];

export const FundingGroupCard = ({ group }) => (
  <div
    data-testid={`funding-group-${group.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    className="bg-white rounded-lg border border-[var(--ac-line)] p-5 md:p-6 shadow-[0_2px_10px_rgba(15,39,80,0.05)]"
  >
    <h3
      className="text-[15px] md:text-[17px] font-bold uppercase tracking-[0.05em] text-[var(--ac-red)] mb-4 pb-3 border-b border-[var(--ac-line)]"
      style={{ fontFamily: "var(--font-display)" }}
    >
      {group.title}
    </h3>
    <ul className="space-y-2.5">
      {group.items.map((it) => {
        const cls =
          "inline-flex items-center gap-1.5 text-[14px] font-semibold text-[var(--ac-royal)] hover:text-[var(--ac-red)] transition";
        return (
          <li key={it.label}>
            {it.external ? (
              <a href={it.to} target="_blank" rel="noopener noreferrer" className={cls}>
                {it.label} <ExternalLink className="w-3.5 h-3.5" />
              </a>
            ) : (
              <Link to={it.to} className={cls}>
                {it.label} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  </div>
);

// Grid wrapper used both on the home page (FinancialAid section) and on the
// FA landing page (between the intro paragraphs and the long-form details).
export const FundingGroupsGrid = () => (
  <div
    className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6"
    data-testid="funding-groups"
  >
    {FUNDING_GROUPS.map((g) => (
      <FundingGroupCard key={g.title} group={g} />
    ))}
  </div>
);
